import React, { useEffect, useMemo, useState } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { Link } from 'react-router-dom';
import {
  FileText, Shield, Cookie, Settings2, RefreshCcw, Plane, Package, Mail,
  Download, XCircle, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import { LEGAL_LINKS } from '../../config/legalLinks';
import api from '../../utils/api';

/**
 * TREATX-LEGAL-PLATFORM-V1_1
 * Patient → Legal & Consent Centre
 *
 * Retains the existing route `/patient/settings/legal` per R2 §6.
 * Renders three sections powered ONLY by canonical services:
 *   1. Legal documents (links to public resolver-backed pages)
 *   2. My consents (read-only history from /api/user/consents/history)
 *   3. Medical consent modules (filtered mcp.* rows)
 *
 * Never writes a consent row. Withdrawals go via
 * POST /api/user/consents/{id}/withdraw (existing canonical endpoint).
 */
const ICON_BY_ROUTE = {
  '/terms': FileText,
  '/privacy': Shield,
  '/cookie-policy': Cookie,
  '/cookie-settings': Settings2,
  '/refund-policy': RefreshCcw,
  '/health-tourism-info': Plane,
  '/open-source': Package,
  '/contact': Mail,
};

const StatusPill = ({ withdrawn, outdated }) => {
  if (withdrawn) return <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-100" data-testid="consent-status-withdrawn"><XCircle className="inline h-3 w-3 mr-1" />Withdrawn</span>;
  if (outdated) return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100" data-testid="consent-status-outdated"><AlertTriangle className="inline h-3 w-3 mr-1" />Outdated</span>;
  return <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100" data-testid="consent-status-current"><CheckCircle2 className="inline h-3 w-3 mr-1" />Current</span>;
};

const PatientLegalSettings = () => {
  // POST-PAYMENT-TRUTH-01 — history and medical modules are TWO independent
  // reads. They shared one loading flag and one error flag through
  // `Promise.all`, so either failure wiped both sections, and the medical
  // section then stated "you have not yet accepted any medical-consent
  // modules" — a failed request presented as a legal fact. Each section now
  // carries its own loading / loaded / failed state, and an export failure no
  // longer masquerades as a history failure.
  const [historyState, setHistoryState] = useState('loading');
  const [modulesState, setModulesState] = useState('loading');
  const [exportError, setExportError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [consents, setConsents] = useState([]);
  const [modules, setModules] = useState([]);

  useEffect(() => {
    let mounted = true;
    setHistoryState('loading');
    setModulesState('loading');
    api.get('/user/consents/history')
      .then((res) => {
        if (!mounted) return;
        // POST-PAYMENT-TRUTH-02 — `res?.data?.consents || []` turned a
        // malformed or unsuccessful answer into the legal statement "No
        // consents recorded yet". The documented shape is
        // {success: true, count, consents: []}; anything else is a FAILURE.
        const body = res?.data;
        if (!body || body.success === false || !Array.isArray(body.consents)) {
          setHistoryState('failed');
          return;
        }
        setConsents(body.consents.filter((c) => !(c.content_key || '').startsWith('mcp.')));
        setHistoryState('loaded');
      })
      .catch(() => { if (mounted) setHistoryState('failed'); });
    api.get('/user/consents/history?module=mcp')
      .then((res) => {
        if (!mounted) return;
        const body = res?.data;
        if (!body || body.success === false || !Array.isArray(body.consents)) {
          setModulesState('failed');
          return;
        }
        setModules(body.consents);
        setModulesState('loaded');
      })
      .catch(() => { if (mounted) setModulesState('failed'); });
    return () => { mounted = false; };
  }, [reloadTick]);

  const exportEvidence = async () => {
    setExportError(null);
    try {
      const res = await api.get('/user/consents/export');
      // An evidence pack with no `consents` array is not a pack: downloading it
      // would hand the patient a file that silently states they consented to
      // nothing. A malformed answer is an export FAILURE.
      if (!res?.data || !Array.isArray(res.data.consents)) {
        setExportError('Evidence export failed. Please try again.');
        return;
      }
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `treatx-consent-evidence-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_e) {
      setExportError('Evidence export failed. Please try again.');
    }
  };

  const groupedConsents = useMemo(() => {
    const g = {};
    for (const c of consents) {
      const k = c.content_key || 'unknown';
      if (!g[k]) g[k] = [];
      g[k].push(c);
    }
    return g;
  }, [consents]);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="patient-legal-settings">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Heading */}
        <header>
          <h1 className="text-2xl font-semibold text-slate-900" data-testid="legal-heading">Legal &amp; Consent Centre</h1>
          <p className="mt-1 text-sm text-slate-500">
            Everything TreatX has legally agreed with you — always the same on Web, iOS and Android.
          </p>
        </header>

        {/* Section 1 — Legal documents */}
        <section data-testid="section-legal-documents">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Legal documents</h2>
          <ul className="divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
            {LEGAL_LINKS.map(({ to, label, testid }) => {
              const Icon = ICON_BY_ROUTE[to] || FileText;
              return (
                <li key={to}>
                  <Link
                    to={to}
                    data-testid={`patient-${testid}`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50"
                  >
                    <Icon className="h-4 w-4 text-slate-500" aria-hidden="true" />
                    <span className="text-sm text-slate-800 flex-1">{label}</span>
                    <span className="text-slate-400" aria-hidden="true">›</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>

        {/* Section 2 — My consents */}
        <section data-testid="section-my-consents">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-700">My consents</h2>
            <button
              type="button"
              onClick={exportEvidence}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50"
              data-testid="export-evidence-btn"
            >
              <Download className="h-3.5 w-3.5" /> Export evidence pack
            </button>
          </div>
          {exportError && (
            <p className="text-sm text-red-600 mb-2" data-testid="consents-export-error">{exportError}</p>
          )}
          {historyState === 'loading' && <p className="text-sm text-slate-500" data-testid="consents-loading">Loading your consents…</p>}
          {historyState === 'failed' && (
            <div className="text-sm text-red-600" data-testid="consents-error">
              <p>Unable to load your consent history right now.</p>
              <button
                type="button"
                onClick={() => setReloadTick((n) => n + 1)}
                className="mt-2 text-xs px-2 py-1 rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                data-testid="consents-retry-btn"
              >
                Try again
              </button>
            </div>
          )}
          {historyState === 'loaded' && Object.keys(groupedConsents).length === 0 && (
            <p className="text-sm text-slate-500" data-testid="consents-empty">No consents recorded yet.</p>
          )}
          {historyState === 'loaded' && Object.keys(groupedConsents).length > 0 && (
            <ul className="divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
              {Object.entries(groupedConsents).map(([key, rows]) => {
                const latest = rows[0]; // history is sorted desc
                return (
                  <li key={key} className="px-4 py-3" data-testid={`consent-row-${key}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-sm font-medium text-slate-800">{key}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          v{latest.content_version} · {formatDateTimeLocale(latest.consented_at)} · {latest.journey_key || latest.flow}
                        </div>
                      </div>
                      <StatusPill withdrawn={latest.withdrawn} outdated={false} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Section 3 — Medical consent modules */}
        <section data-testid="section-medical-consent-modules">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Medical consent modules</h2>
          {modulesState === 'loading' && <p className="text-sm text-slate-500" data-testid="mcp-loading">Loading…</p>}
          {modulesState === 'failed' && (
            <div className="text-sm text-red-600" data-testid="mcp-error">
              <p>Unable to load your medical-consent modules right now. This does not mean you have not accepted any.</p>
              <button
                type="button"
                onClick={() => setReloadTick((n) => n + 1)}
                className="mt-2 text-xs px-2 py-1 rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                data-testid="mcp-retry-btn"
              >
                Try again
              </button>
            </div>
          )}
          {modulesState === 'loaded' && modules.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="mcp-empty">
              You have not yet accepted any medical-consent modules. These are captured when you upload a medical document, provide health information, or share a before/after image.
            </p>
          )}
          {modulesState === 'loaded' && modules.length > 0 && (
            <ul className="divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
              {modules.map((m) => (
                <li key={m.consent_id} className="px-4 py-3" data-testid={`mcp-row-${m.content_key}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-sm font-medium text-slate-800">{m.content_key}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        v{m.content_version} · {formatDateTimeLocale(m.consented_at)} · {m.journey_key || m.flow}
                        {m.event_id ? <> · event {String(m.event_id).slice(0, 10)}…</> : null}
                      </div>
                    </div>
                    <StatusPill withdrawn={m.withdrawn} outdated={false} />
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-xs text-slate-400">
            Each module is captured independently — accepting one never implies the others.
          </p>
        </section>

        <p className="text-xs text-slate-400">
          Same legal content across Web, iOS and Android — you never see two versions of the same document.
        </p>
      </div>
    </div>
  );
};

export default PatientLegalSettings;
